import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerWorkspaceNavigation from "./components/OwnerWorkspaceNavigation";
import OwnerClientPlatformDetail from "./clients/OwnerClientPlatformDetail";
import OwnerClientLinkDialog from "./clients/OwnerClientLinkDialog";
import { surfaceSx } from "./center/ownerCenterUi";

function idOf(value) {
  return String(value?.id || value?._id || "");
}

export default function OwnerClientsPage() {
  const [clients, setClients] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [selectedId, setSelectedId] = React.useState("");
  const [platformContext, setPlatformContext] = React.useState(null);
  const [crmContext, setCrmContext] = React.useState(null);
  const [loadingList, setLoadingList] = React.useState(true);
  const [loadingPlatform, setLoadingPlatform] = React.useState(false);
  const [loadingCrm, setLoadingCrm] = React.useState(false);
  const [error, setError] = React.useState("");
  const [platformError, setPlatformError] = React.useState("");
  const [crmError, setCrmError] = React.useState("");
  const [linkOpen, setLinkOpen] = React.useState(false);
  const platformRequestRef = React.useRef(0);
  const crmRequestRef = React.useRef(0);

  const loadClients = React.useCallback(async () => {
    setLoadingList(true);
    setError("");
    try {
      const response = await ownerApi.listClients({ limit: 250 });
      const rows = response?.clients || [];
      setClients(rows);
      setSelectedId((current) => current && rows.some((row) => idOf(row) === current) ? current : idOf(rows[0]));
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tus clientes."));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadPlatform = React.useCallback(async (clientId, { clear = false } = {}) => {
    const requestId = ++platformRequestRef.current;
    if (!clientId) {
      setPlatformContext(null);
      setPlatformError("");
      setLoadingPlatform(false);
      return;
    }
    if (clear) setPlatformContext(null);
    setLoadingPlatform(true);
    setPlatformError("");
    try {
      const response = await ownerApi.clientWebProfitContext(clientId);
      if (requestId === platformRequestRef.current) setPlatformContext(response?.context || null);
    } catch (err) {
      if (requestId === platformRequestRef.current) setPlatformError(formatApiError(err, "No pude cargar la relación con WebProfit."));
    } finally {
      if (requestId === platformRequestRef.current) setLoadingPlatform(false);
    }
  }, []);

  const loadCrm = React.useCallback(async (clientId, { clear = false } = {}) => {
    const requestId = ++crmRequestRef.current;
    if (!clientId) {
      setCrmContext(null);
      setCrmError("");
      setLoadingCrm(false);
      return;
    }
    if (clear) setCrmContext(null);
    setLoadingCrm(true);
    setCrmError("");
    try {
      const response = await ownerApi.personContext(clientId);
      if (requestId === crmRequestRef.current) setCrmContext(response?.context || null);
    } catch (err) {
      if (requestId === crmRequestRef.current) setCrmError(formatApiError(err, "No pude cargar actividad, dinero y horas del cliente."));
    } finally {
      if (requestId === crmRequestRef.current) setLoadingCrm(false);
    }
  }, []);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  React.useEffect(() => {
    if (!selectedId) {
      setCrmContext(null);
      setPlatformContext(null);
      return;
    }
    loadCrm(selectedId, { clear: true });
    loadPlatform(selectedId, { clear: true });
  }, [selectedId, loadCrm, loadPlatform]);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = clients.filter((client) => !normalizedQuery || [client.name, client.company, client.email, client.phone].some((value) => String(value || "").toLowerCase().includes(normalizedQuery)));
  const selected = clients.find((row) => idOf(row) === selectedId) || null;

  const handleClientUpdated = (nextClient) => {
    const nextId = idOf(nextClient) || selectedId;
    setClients((current) => current.map((row) => idOf(row) === nextId ? { ...row, ...nextClient } : row));
    if (nextId === selectedId) loadPlatform(selectedId);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto", pb: 5 }}>
      <OwnerWorkspaceNavigation />
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "flex-end" }} sx={{ mb: 1.6 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "1.55rem", md: "2rem" }, fontWeight: 1000, letterSpacing: "-.035em" }}>Clientes</Typography>
          <Typography sx={{ mt: .3, color: "text.secondary", fontSize: ".84rem", maxWidth: 760 }}>
            Una ficha para identidad, próximos pasos, historial, dinero, mensualidades, horas y relaciones reales con WebProfit.
          </Typography>
        </Box>
        <Chip icon={<CloudDoneRoundedIcon />} label="Cliente 360 · datos conectados" variant="outlined" sx={{ fontWeight: 900 }} />
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 380px) minmax(0, 1fr)" }, gap: 1.4, alignItems: "start" }}>
        <Box sx={{ ...surfaceSx, p: 1.2, position: { lg: "sticky" }, top: { lg: 12 }, maxHeight: { lg: "calc(100vh - 120px)" }, overflow: "hidden" }}>
          <TextField fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar persona, empresa, email…" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
          <Typography sx={{ px: .6, pt: 1.1, pb: .65, color: "text.secondary", fontSize: ".69rem", fontWeight: 900 }}>{filtered.length} PERSONAS</Typography>
          <Stack spacing={.45} sx={{ maxHeight: { lg: "calc(100vh - 215px)" }, overflowY: "auto", pr: .2 }}>
            {loadingList ? <Box sx={{ py: 7, textAlign: "center" }}><CircularProgress size={28} /></Box> : filtered.map((client) => {
              const id = idOf(client);
              const active = id === selectedId;
              return (
                <Box
                  key={id}
                  role="button"
                  tabIndex={0}
                  aria-current={active ? "true" : undefined}
                  onClick={() => setSelectedId(id)}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedId(id); } }}
                  sx={{ px: 1.15, py: .95, borderRadius: 2.2, cursor: "pointer", bgcolor: active ? "action.selected" : "transparent", border: "1px solid", borderColor: active ? "primary.main" : "transparent", transition: "120ms ease", "&:hover": { bgcolor: active ? "action.selected" : "action.hover" } }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.hover", flex: "0 0 auto" }}><PersonRoundedIcon sx={{ fontSize: 18 }} /></Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: ".84rem", fontWeight: 950 }}>{client.name}</Typography>
                      <Typography noWrap sx={{ color: "text.secondary", fontSize: ".7rem" }}>{client.company || client.email || client.relationshipType || "Persona"}</Typography>
                    </Box>
                    {client.webProfitUserId ? <CloudDoneRoundedIcon color="success" sx={{ fontSize: 17 }} aria-label="Cuenta WebProfit vinculada" /> : null}
                  </Stack>
                </Box>
              );
            })}
            {!loadingList && !filtered.length ? <Typography sx={{ py: 5, textAlign: "center", color: "text.secondary", fontSize: ".8rem" }}>No encontré personas.</Typography> : null}
          </Stack>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {selected ? (
            <OwnerClientPlatformDetail
              client={selected}
              platformContext={platformContext}
              crmContext={crmContext}
              loadingCrm={loadingCrm}
              loadingPlatform={loadingPlatform}
              crmError={crmError}
              platformError={platformError}
              onLinkUser={() => setLinkOpen(true)}
              onRefreshCrm={() => loadCrm(selectedId)}
              onRefreshPlatform={() => loadPlatform(selectedId)}
              onClientUpdated={handleClientUpdated}
              onPlatformContextUpdated={(nextContext) => setPlatformContext(nextContext)}
            />
          ) : <Box sx={{ ...surfaceSx, py: 12, textAlign: "center" }}><Typography sx={{ color: "text.secondary" }}>Elegí una persona para ver su ficha 360.</Typography></Box>}
        </Box>
      </Box>

      <OwnerClientLinkDialog
        open={linkOpen}
        client={selected}
        context={platformContext}
        onClose={() => setLinkOpen(false)}
        onLinked={(next, linkedClient) => {
          setPlatformContext(next);
          if (linkedClient) {
            const linkedId = idOf(linkedClient);
            setClients((current) => current.map((row) => idOf(row) === linkedId ? { ...row, ...linkedClient } : row));
          }
        }}
      />
    </Box>
  );
}
